import { BaseAsyncValue } from '@ordersnproducts/modules/shared/models/async-value';
import { Observable } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';
import * as moment from 'moment';
import { getOrderSelectedDetail } from '@ordersnproducts/modules/new-modify-order/selectors/order-selected-detail.selector';
import { isSameDay } from 'app/calendar-view/utils';
import { _safe } from '@ordersnproducts/modules/shared/utils/safe-get.method';

const CUT_OFF_TIME_UK_IMPROVEMENTS_MODIFY = 12;
const CUT_OFF_DAYS_UK = 1;
const DEFAULT_MAX_VALUE = 1000;
const DEFAULT_MIN_VALUE = 1;
const DECREASE_PERCENT_MIN_VALUE = 0.25;
const UK_TIMEZONE = 'Europe/London';

/**
 * This provider is for the new improvements modify rules for the UK that has new different cut off time for orders
 * to be modified, new time is 12:00 UK time
 * This provider is practically the same as the old one, but this one is connected to feature toggle service so it can be
 * turned off any time by feature toggle
 */
export namespace ImprovementsModifyProvider {
  export const isAfterCutOffTimeUK = (programmedDateTime) => {
    const ukTime = moment().tz(UK_TIMEZONE);
    const nextDate = ukTime.clone().add(CUT_OFF_DAYS_UK, 'days').format('YYYY-MM-DD');
    const localDate = ukTime.format('YYYY-MM-DD');
    const hour = ukTime.hour();
    const plantDate = moment(programmedDateTime).tz(UK_TIMEZONE).format('YYYY-MM-DD');
    const isSame = isSameDay(localDate, plantDate);

    return (nextDate === plantDate && hour >= CUT_OFF_TIME_UK_IMPROVEMENTS_MODIFY) || isSame;
  };

  /**
   * This class is used to validate if the field should be disabled based on the feature toggle
   * that is enabled in the UK by the new improvements modify rules
   */
  export class ShouldDisabledBecauseOfCutOffTime extends BaseAsyncValue<Boolean> {
    getValue(): Observable<Boolean> {
      return this.store.select(getOrderSelectedDetail).pipe(
        takeUntil(this.ngDestroy),
        filter((orderDetail) => !!orderDetail),
        map((orderDetail) => {
          if (orderDetail && orderDetail.programmedDateTime) {
            return isAfterCutOffTimeUK(orderDetail.programmedDateTime);
          } else {
            return false;
          }
        }),
      );
    }
  }

  /**
   * This class is used to validate if the field should be enabled based on the feature toggle
   * that is enabled in the UK by the new improvements modify rules
   */
  export class ShouldEnableBecauseOfCutOffTime extends BaseAsyncValue<Boolean> {
    getValue(): Observable<Boolean> {
      return this.store.select(getOrderSelectedDetail).pipe(
        takeUntil(this.ngDestroy),
        filter((orderDetail) => !!orderDetail),
        map((orderDetail) => {
          if (orderDetail && orderDetail.programmedDateTime) {
            return !isAfterCutOffTimeUK(orderDetail.programmedDateTime);
          } else {
            return false;
          }
        }),
      );
    }
  }

  /**
   * This class is used to validate if the part load should be disabled based on the feature toggle
   * and the new improvements modify rules for orders
   */
  export class ShouldDisabledPartLoadUk extends BaseAsyncValue<Boolean> {
    getValue(): Observable<Boolean> {
      return this.store.select(getOrderSelectedDetail).pipe(
        takeUntil(this.ngDestroy),
        filter((orderDetail) => !!orderDetail),
        map((orderDetail) => {
          if (
            _safe(() => orderDetail.orderProducts[0].itemProperties['cleanUp']) === false &&
            isAfterCutOffTimeUK(orderDetail.programmedDateTime)
          ) {
            return true;
          } else {
            return false;
          }
        }),
      );
    }
  }

  /**
   * This class is used to let user select max quantity based on the cut off time
   */
  export class MaxQuantityUk extends BaseAsyncValue<number> {
    getValue(): Observable<number> {
      return this.store.select(getOrderSelectedDetail).pipe(
        takeUntil(this.ngDestroy),
        filter((orderDetail) => !!orderDetail),
        map((orderDetail) => {
          if (orderDetail && orderDetail.programmedDateTime) {
            const isAfterValidTime = isAfterCutOffTimeUK(orderDetail.programmedDateTime);

            return isAfterValidTime ? orderDetail.totalQuantity : DEFAULT_MAX_VALUE;
          } else {
            return DEFAULT_MAX_VALUE;
          }
        }),
      );
    }
  }

  /**
   * This class is used to let user select min quantity based on the cut off time
   */
  export class MinQuantityUk extends BaseAsyncValue<number> {
    getValue(): Observable<number> {
      return this.store.select(getOrderSelectedDetail).pipe(
        takeUntil(this.ngDestroy),
        filter((orderDetail) => !!orderDetail),
        map((orderDetail) => {
          if (orderDetail && orderDetail.programmedDateTime) {
            const isAfterValidTime = isAfterCutOffTimeUK(orderDetail.programmedDateTime);
            const quantityValue = orderDetail.totalQuantity;
            const minQuantityValue = quantityValue - Math.round(quantityValue * DECREASE_PERCENT_MIN_VALUE * 2) / 2;

            // Ensure the minimum quantity is at least 1
            return isAfterValidTime ? Math.max(minQuantityValue, 1) : DEFAULT_MIN_VALUE;
          } else {
            return DEFAULT_MIN_VALUE;
          }
        }),
      );
    }
  }
}
